import { prettyErrorDescription } from 'constants/errors'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import formServices from 'services/form-builder-services'
import toast from 'react-hot-toast'
import textServices from 'services/text-services'
import { Label, SelectGender } from 'theme/theme'
import Input from 'components/input'
import { useContext, useEffect, useRef, useState } from 'react'
import { LoaderContext } from 'contexts/loader-context'
import ProfileService from 'services/profile-services'
import { dlTrackError, dlTrackEvent } from 'components/dataLayer/dataLayer'
import { AxiosError } from 'axios'
import CroppImage from 'components/croppImage'
import dayjs from 'dayjs'
import { useAgegate } from 'hooks/use-agegate'
import { useSession } from 'contexts/session-context'
import { useLocalStorage } from 'react-use'
import { usePopupContext } from 'hooks/use-main-cass'

interface IProps {
  defaultValues: { imageUser?: string, firstName?: string, lastName?: string, nickName?: string, birthday?: string, genreId?: number }
}

const EditProfileV2 = ({ defaultValues }: IProps) => {
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: yupResolver(formServices.formGenerateSchema(['firstName', 'lastName', 'nickName', 'birthdayDate', 'genreId'])),
    criteriaMode: 'firstError',
    reValidateMode: 'onSubmit',
    mode: 'all'
  })
  const { closePopup } = usePopupContext()
  const { setLoad } = useContext(LoaderContext)
  const [gender, setGender] = useState([])
  const genreId = watch('genreId')
  const renderImage = watch('renderImage')
  const croppedImage = watch('croppedImage')
  const [openCropped, setOpenCropped] = useState(false)
  const ref = useRef<HTMLInputElement>(null)
  const { setAgeLocalForm } = useAgegate()
  const [{ data: session }, { setUser }] = useSession()
  const [state, setState] = useLocalStorage('state', {})

  useEffect(() => {
    if (defaultValues) {
      setValue('firstName', defaultValues?.firstName)
      setValue('lastName', defaultValues?.lastName)
      setValue('nickName', defaultValues?.nickName)
      if (defaultValues?.birthday) {
        const birthdayDate = dayjs(defaultValues?.birthday).format('YYYY-MM-DD')
        setValue('birthdayDate', birthdayDate)
      }
      setValue('genreId', defaultValues?.genreId)

      setValue('croppedImage', defaultValues?.imageUser ? defaultValues?.imageUser : `${AZURE_BASE_URL}/menu_offline_user-icon.webp`)
    }
  }, [defaultValues])

  const handleError = (error: AxiosError) => {
    if (error?.isAxiosError) {
      const { code: errorCode, message: errorMessage } = error?.response?.data ?? {}
      dlTrackError({
        error_type: 'login',
        error_code: { errorCode },
        error_message: { errorMessage },
        section: 'login'
      })
      toast.error(prettyErrorDescription(errorCode), { className: 'toast__error' })
    }
  }

  async function onFormSubmit({ birthdayDate, nickName, firstName, lastName, genreId, fileImage, ...data }: any) {
    setLoad(true)
    try {
      const birthday = new Date(birthdayDate)
      const response = setAgeLocalForm(birthday)
      if (response?.error) return toast.error(response?.error, { className: 'toast__error' })
      if (data?.nickName) await ProfileService.validateNickName(data?.nickName)

      const formData = new FormData()
      formData.append('Files', fileImage as File)
      if (fileImage) {
        formData.append('file', fileImage, 'profile-image.png');
        formData.append('input', JSON.stringify({ name: '', ImageFileName: 'profile-image.png', ImageContentType: 'image/png' }));
        await ProfileService.updateImage(formData, session)
      }

      const payload = { user: { birthday: birthdayDate, genreId, lastName, firstName, nickName }, session }

      const responseUpdate = await ProfileService.updateProfile(payload)
      setUser(responseUpdate.data)
      setState({ ...state, user: responseUpdate.data })
      toast.success('Los cambios se guardaron con éxito', { className: 'toast__success' })
      closePopup()
    } catch (error) {
      handleError(error)
    } finally {
      setLoad(false)
    }
  }

  async function getGenres() {
    setLoad(true)
    try {
      const response = await ProfileService.genres()
      setGender(response.data)
    } catch (error) {
      handleError(error)
    } finally {
      setLoad(false)
    }
  }

  useEffect(() => {
    getGenres()
  }, [])

  const onInvalid = (errors: Record<string, any>) => {
    if (errors) {
      const errorsKeys = Object.values(errors)
      toast.error(errorsKeys[0]?.message, { className: 'toast__error' })
    }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const regexType = /^image\/(png|jpeg|jpg|heic|webp)$/;
    if (!regexType.test(file?.type || '')) return toast.error('El formato de imagen no es valido', { className: 'toast__error' })
    if (file) {
      setValue('file', file)
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        setValue('renderImage', reader.result)
        setOpenCropped(true)
      }
    }
  }

  const toggleCropped = () => {
    setOpenCropped(false)
    ref.current.value = null
  }

  const setCroppedImage = (value: string, file: File) => {
    setValue('croppedImage', value)
    setValue('fileImage', file)
  }

  return (
    <div className="pb-4">
      {openCropped && (
        <CroppImage imageSrc={renderImage} setCroppedImage={setCroppedImage} toggle={toggleCropped} />
      )}
      <h2 className="form__sign-in__title">Editar mis datos</h2>
      <form onSubmit={handleSubmit(onFormSubmit, onInvalid)} className='space-y-4'>
        <div className={`flex gap-4 input-container`}>
          <div className='relative rounded-full flex items-center justify-center' style={{ height: '80px', width: '80px' }}  onClick={() => ref?.current?.click()}>
            <img src={croppedImage} alt='Image profile' className='w-full h-full rounded-full object-cover' style={{ aspectRatio: '1/1' }} />
            <div className='absolute w-full h-full bg-black bg-opacity-30 border-2 border-solid border-white flex items-center justify-center rounded-full'>
              <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M26.25 18.502V23.502C26.25 24.165 25.9866 24.8009 25.5178 25.2697C25.0489 25.7386 24.413 26.002 23.75 26.002H6.25C5.58696 26.002 4.95107 25.7386 4.48223 25.2697C4.01339 24.8009 3.75 24.165 3.75 23.502V18.502M21.25 9.75195L15 3.50195M15 3.50195L8.75 9.75195M15 3.50195V18.502" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
          <label className='flex flex-col gap-2 flex-1'>
            <button type='button' onClick={() => ref?.current?.click()} className='button-edit big'><p>Cambiar foto de perfil</p></button>
            <input type='file' ref={ref} className='hidden' accept='.png' onChange={handleUpload} />
            <ul className='list-disc pl-5'>
              <li className='text small'>Usa una imagen cuadrada para mejores resultados. </li>
              <li className='text small'>Tiene que tener 400x400 px cómo máximo.</li>
            </ul>
          </label>
        </div>
        <div className={`gap-2 flex flex-col input-container ${errors?.firstName?.message ? 'error-input' : ''}`}>
          <div className='flex items-center gap-2'>
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.5 0.21875C6.0957 0.21875 6.64258 0.365234 7.14062 0.658203C7.64844 0.951172 8.04883 1.35156 8.3418 1.85938C8.63477 2.35742 8.78125 2.9043 8.78125 3.5C8.78125 4.05664 8.64941 4.57422 8.38574 5.05273C8.13184 5.53125 7.78027 5.92188 7.33105 6.22461C7.8877 6.46875 8.38086 6.80566 8.81055 7.23535C9.25 7.66504 9.58691 8.1582 9.82129 8.71484C10.0654 9.29102 10.1875 9.89648 10.1875 10.5312H9.25C9.25 9.84766 9.0791 9.22266 8.7373 8.65625C8.40527 8.08008 7.95117 7.62598 7.375 7.29395C6.80859 6.95215 6.18359 6.78125 5.5 6.78125C4.81641 6.78125 4.18652 6.95215 3.61035 7.29395C3.04395 7.62598 2.58984 8.08008 2.24805 8.65625C1.91602 9.22266 1.75 9.84766 1.75 10.5312H0.8125C0.8125 9.89648 0.93457 9.29102 1.17871 8.71484C1.41309 8.1582 1.74512 7.66504 2.1748 7.23535C2.61426 6.80566 3.1123 6.46875 3.66895 6.22461C3.21973 5.92188 2.86328 5.53125 2.59961 5.05273C2.3457 4.57422 2.21875 4.05664 2.21875 3.5C2.21875 2.9043 2.36523 2.35742 2.6582 1.85938C2.95117 1.35156 3.34668 0.951172 3.84473 0.658203C4.35254 0.365234 4.9043 0.21875 5.5 0.21875ZM5.5 1.15625C5.07031 1.15625 4.6748 1.26367 4.31348 1.47852C3.96191 1.68359 3.67871 1.9668 3.46387 2.32812C3.25879 2.67969 3.15625 3.07031 3.15625 3.5C3.15625 3.92969 3.25879 4.3252 3.46387 4.68652C3.67871 5.03809 3.96191 5.32129 4.31348 5.53613C4.6748 5.74121 5.07031 5.84375 5.5 5.84375C5.92969 5.84375 6.32031 5.74121 6.67188 5.53613C7.0332 5.32129 7.31641 5.03809 7.52148 4.68652C7.73633 4.3252 7.84375 3.92969 7.84375 3.5C7.84375 3.07031 7.73633 2.67969 7.52148 2.32812C7.31641 1.9668 7.0332 1.68359 6.67188 1.47852C6.32031 1.26367 5.92969 1.15625 5.5 1.15625Z" fill="currentColor" />
            </svg>
            <Label className="text-start text-2xl font-bold uppercase input-label">
              {textServices.findByText('profile/form/information/firstname/label', { es: '¿Cómo te llamas?', en: 'What is your name?' })}
            </Label>
          </div>
          <Input
            className={`text-center saira font-bold text-sm`}
            type="text"
            placeholder={textServices.findByText(
              'profile/form/information/firstname/placeholder',
              { es: 'PON TU NOMBRE AQUÍ', en: 'PUT YOUR NAME HERE' }
            )}
            name="firstName"
            {...register('firstName', {
              onBlur: (e) => {
                dlTrackEvent('form_interaction', {
                  form_name: 'datos cuenta',
                  form_field: 'first name',
                  form_action: e.target.value ? 'complete' : 'empty',
                  section: 'sign up'
                })
              }
            })}
          />
        </div>
        <div className={`gap-2 flex flex-col input-container ${errors?.lastName?.message ? 'error-input' : ''}`}>
          <div className='flex items-center gap-2'>
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.5 0.21875C6.0957 0.21875 6.64258 0.365234 7.14062 0.658203C7.64844 0.951172 8.04883 1.35156 8.3418 1.85938C8.63477 2.35742 8.78125 2.9043 8.78125 3.5C8.78125 4.05664 8.64941 4.57422 8.38574 5.05273C8.13184 5.53125 7.78027 5.92188 7.33105 6.22461C7.8877 6.46875 8.38086 6.80566 8.81055 7.23535C9.25 7.66504 9.58691 8.1582 9.82129 8.71484C10.0654 9.29102 10.1875 9.89648 10.1875 10.5312H9.25C9.25 9.84766 9.0791 9.22266 8.7373 8.65625C8.40527 8.08008 7.95117 7.62598 7.375 7.29395C6.80859 6.95215 6.18359 6.78125 5.5 6.78125C4.81641 6.78125 4.18652 6.95215 3.61035 7.29395C3.04395 7.62598 2.58984 8.08008 2.24805 8.65625C1.91602 9.22266 1.75 9.84766 1.75 10.5312H0.8125C0.8125 9.89648 0.93457 9.29102 1.17871 8.71484C1.41309 8.1582 1.74512 7.66504 2.1748 7.23535C2.61426 6.80566 3.1123 6.46875 3.66895 6.22461C3.21973 5.92188 2.86328 5.53125 2.59961 5.05273C2.3457 4.57422 2.21875 4.05664 2.21875 3.5C2.21875 2.9043 2.36523 2.35742 2.6582 1.85938C2.95117 1.35156 3.34668 0.951172 3.84473 0.658203C4.35254 0.365234 4.9043 0.21875 5.5 0.21875ZM5.5 1.15625C5.07031 1.15625 4.6748 1.26367 4.31348 1.47852C3.96191 1.68359 3.67871 1.9668 3.46387 2.32812C3.25879 2.67969 3.15625 3.07031 3.15625 3.5C3.15625 3.92969 3.25879 4.3252 3.46387 4.68652C3.67871 5.03809 3.96191 5.32129 4.31348 5.53613C4.6748 5.74121 5.07031 5.84375 5.5 5.84375C5.92969 5.84375 6.32031 5.74121 6.67188 5.53613C7.0332 5.32129 7.31641 5.03809 7.52148 4.68652C7.73633 4.3252 7.84375 3.92969 7.84375 3.5C7.84375 3.07031 7.73633 2.67969 7.52148 2.32812C7.31641 1.9668 7.0332 1.68359 6.67188 1.47852C6.32031 1.26367 5.92969 1.15625 5.5 1.15625Z" fill="currentColor" />
            </svg>
            <Label className="text-start text-2xl font-bold uppercase input-label">
              {textServices.findByText('profile/form/information/lastname/label', {
                es: 'Apellido',
                en: 'Last name'
              })}
            </Label>
          </div>
          <Input
            className={`text-center saira font-bold text-sm`}
            type="text"
            placeholder={textServices.findByText(
              'profile/form/information/lastname/placeholder',
              { es: 'PON TU APELLIDO AQUÍ', en: 'PUT YOUR LAST NAME HERE' }
            )}
            name="lastName"
            {...register('lastName', {
              onBlur: (e) => {
                dlTrackEvent('form_interaction', {
                  form_name: 'datos cuenta',
                  form_field: 'last name',
                  form_action: e.target.value ? 'complete' : 'empty',
                  section: 'sign up'
                })
              }
            })}
          />
        </div>
        <div className={`gap-2 flex flex-col input-container ${errors?.nickName?.message ? 'error-input' : ''}`}>
          <div className='flex items-center gap-2'>
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.5 0.21875C6.0957 0.21875 6.64258 0.365234 7.14062 0.658203C7.64844 0.951172 8.04883 1.35156 8.3418 1.85938C8.63477 2.35742 8.78125 2.9043 8.78125 3.5C8.78125 4.05664 8.64941 4.57422 8.38574 5.05273C8.13184 5.53125 7.78027 5.92188 7.33105 6.22461C7.8877 6.46875 8.38086 6.80566 8.81055 7.23535C9.25 7.66504 9.58691 8.1582 9.82129 8.71484C10.0654 9.29102 10.1875 9.89648 10.1875 10.5312H9.25C9.25 9.84766 9.0791 9.22266 8.7373 8.65625C8.40527 8.08008 7.95117 7.62598 7.375 7.29395C6.80859 6.95215 6.18359 6.78125 5.5 6.78125C4.81641 6.78125 4.18652 6.95215 3.61035 7.29395C3.04395 7.62598 2.58984 8.08008 2.24805 8.65625C1.91602 9.22266 1.75 9.84766 1.75 10.5312H0.8125C0.8125 9.89648 0.93457 9.29102 1.17871 8.71484C1.41309 8.1582 1.74512 7.66504 2.1748 7.23535C2.61426 6.80566 3.1123 6.46875 3.66895 6.22461C3.21973 5.92188 2.86328 5.53125 2.59961 5.05273C2.3457 4.57422 2.21875 4.05664 2.21875 3.5C2.21875 2.9043 2.36523 2.35742 2.6582 1.85938C2.95117 1.35156 3.34668 0.951172 3.84473 0.658203C4.35254 0.365234 4.9043 0.21875 5.5 0.21875ZM5.5 1.15625C5.07031 1.15625 4.6748 1.26367 4.31348 1.47852C3.96191 1.68359 3.67871 1.9668 3.46387 2.32812C3.25879 2.67969 3.15625 3.07031 3.15625 3.5C3.15625 3.92969 3.25879 4.3252 3.46387 4.68652C3.67871 5.03809 3.96191 5.32129 4.31348 5.53613C4.6748 5.74121 5.07031 5.84375 5.5 5.84375C5.92969 5.84375 6.32031 5.74121 6.67188 5.53613C7.0332 5.32129 7.31641 5.03809 7.52148 4.68652C7.73633 4.3252 7.84375 3.92969 7.84375 3.5C7.84375 3.07031 7.73633 2.67969 7.52148 2.32812C7.31641 1.9668 7.0332 1.68359 6.67188 1.47852C6.32031 1.26367 5.92969 1.15625 5.5 1.15625Z" fill="currentColor" />
            </svg>
            <Label className="text-start text-2xl font-bold uppercase input-label">
              {textServices.findByText('profile/form/information/nickname/label', { es: 'Apodo', en: 'Nickname' })}
            </Label>
          </div>
          <Input
            className={`text-center saira font-bold text-sm`}
            type="text"
            placeholder={textServices.findByText(
              'profile/form/information/nickname/placeholder',
              { es: '¿COMO TE GUSTA QUE TE LLAMEN?', en: 'WHAT DO YOU LIKE TO BE CALLED?' }
            )}
            {...register('nickName', {
              onBlur: (e) => {
                dlTrackEvent('form_interaction', {
                  form_name: 'datos cuenta',
                  form_field: 'nickname',
                  form_action: e.target.value ? 'complete' : 'empty',
                  section: 'sign up'
                })
              }
            })}
          />
        </div>
        <div className={`gap-2 flex flex-col input-container ${errors?.birthdayDate?.message ? 'error-input' : ''}`}>
          <div className='flex items-center gap-2'>
            <svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.21875 0.75H3.15625V1.21875H7.84375V0.75H8.78125V1.21875H10.6562V11.5312H0.34375V1.21875H2.21875V0.75ZM1.28125 2.15625V3.09375H9.71875V2.15625H8.78125V2.625H7.84375V2.15625H3.15625V2.625H2.21875V2.15625H1.28125ZM1.28125 4.03125V10.5938H9.71875V4.03125H1.28125ZM4.09375 4.96875H5.03125V5.90625H4.09375V4.96875ZM5.96875 4.96875H6.90625V5.90625H5.96875V4.96875ZM7.84375 4.96875H8.78125V5.90625H7.84375V4.96875ZM2.21875 6.84375H3.15625V7.78125H2.21875V6.84375ZM4.09375 6.84375H5.03125V7.78125H4.09375V6.84375ZM5.96875 6.84375H6.90625V7.78125H5.96875V6.84375ZM7.84375 6.84375H8.78125V7.78125H7.84375V6.84375ZM2.21875 8.71875H3.15625V9.65625H2.21875V8.71875ZM4.09375 8.71875H5.03125V9.65625H4.09375V8.71875ZM5.96875 8.71875H6.90625V9.65625H5.96875V8.71875Z" fill="currentColor" />
            </svg>
            <Label className="text-start text-2xl font-bold uppercase input-label">
              {textServices.findByText('profile/form/information/birthday/label', { es: 'Fecha de nacimiento', en: 'Date of birthday' })}
            </Label>
          </div>
          <input
            type='date'
            className='input-date'
            {...register('birthdayDate', {
              onBlur: (e) => {
                dlTrackEvent('form_interaction', {
                  form_field: 'birthday',
                  form_name: 'datos cuenta',
                  form_action: e.target.value ? 'complete' : 'empty',
                  section: 'sign up'
                })
              }
            })}
          />
        </div>
        <div className={`gap-2 flex flex-col input-container ${errors?.genreId?.message ? 'error-input' : ''}`}>
          <div className='flex items-center gap-2'>
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.5 0.21875C6.0957 0.21875 6.64258 0.365234 7.14062 0.658203C7.64844 0.951172 8.04883 1.35156 8.3418 1.85938C8.63477 2.35742 8.78125 2.9043 8.78125 3.5C8.78125 4.05664 8.64941 4.57422 8.38574 5.05273C8.13184 5.53125 7.78027 5.92188 7.33105 6.22461C7.8877 6.46875 8.38086 6.80566 8.81055 7.23535C9.25 7.66504 9.58691 8.1582 9.82129 8.71484C10.0654 9.29102 10.1875 9.89648 10.1875 10.5312H9.25C9.25 9.84766 9.0791 9.22266 8.7373 8.65625C8.40527 8.08008 7.95117 7.62598 7.375 7.29395C6.80859 6.95215 6.18359 6.78125 5.5 6.78125C4.81641 6.78125 4.18652 6.95215 3.61035 7.29395C3.04395 7.62598 2.58984 8.08008 2.24805 8.65625C1.91602 9.22266 1.75 9.84766 1.75 10.5312H0.8125C0.8125 9.89648 0.93457 9.29102 1.17871 8.71484C1.41309 8.1582 1.74512 7.66504 2.1748 7.23535C2.61426 6.80566 3.1123 6.46875 3.66895 6.22461C3.21973 5.92188 2.86328 5.53125 2.59961 5.05273C2.3457 4.57422 2.21875 4.05664 2.21875 3.5C2.21875 2.9043 2.36523 2.35742 2.6582 1.85938C2.95117 1.35156 3.34668 0.951172 3.84473 0.658203C4.35254 0.365234 4.9043 0.21875 5.5 0.21875ZM5.5 1.15625C5.07031 1.15625 4.6748 1.26367 4.31348 1.47852C3.96191 1.68359 3.67871 1.9668 3.46387 2.32812C3.25879 2.67969 3.15625 3.07031 3.15625 3.5C3.15625 3.92969 3.25879 4.3252 3.46387 4.68652C3.67871 5.03809 3.96191 5.32129 4.31348 5.53613C4.6748 5.74121 5.07031 5.84375 5.5 5.84375C5.92969 5.84375 6.32031 5.74121 6.67188 5.53613C7.0332 5.32129 7.31641 5.03809 7.52148 4.68652C7.73633 4.3252 7.84375 3.92969 7.84375 3.5C7.84375 3.07031 7.73633 2.67969 7.52148 2.32812C7.31641 1.9668 7.0332 1.68359 6.67188 1.47852C6.32031 1.26367 5.92969 1.15625 5.5 1.15625Z" fill="currentColor" />
            </svg>
            <Label className="text-start text-2xl font-bold uppercase input-label">
              {textServices.findByText('profile/form/information/gender/label', { es: '¿Cómo te identificas?', en: 'How do you identify yourself?' })}
            </Label>
          </div>
          <SelectGender
            name="genreId"
            {...register('genreId', {
              onBlur: (e) => {
                dlTrackEvent('form_interaction', {
                  form_field: 'genero',
                  form_action: e.target.value ? 'complete' : 'empty',
                  form_name: 'datos personales',
                  section: 'sign up'
                })
              }
            })}
            className="text-xs font-bold p-4 w-full min-h-2 mx-auto saira uppercase input-select"
          >
            <option value="">{textServices.findByText('profile/form/information/gender/placeholder', { es: 'Selecciona como te consideras', en: 'Select how you consider yourself' })}</option>
            {gender?.map((item: { id: number; name: string }) => (
              <option key={item.id} value={item.id} selected={item.id === Number(genreId) || genreId === item?.name}>
                {item?.name}
              </option>
            ))}
          </SelectGender>
        </div>
        <div className='space-y-2'>
          <button type="submit" className="form__sign-in__btn form__sign-in__btn__main">
            Guardar cambios
          </button>
        </div>
      </form>
    </div>
  )
}

export default EditProfileV2
